// NPM Imports
import React, { useEffect, useRef } from "react";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

/**
 *
 * @param {*}
 * "defaultListItemHeight" : default height of list item, will be used when dynamic height calculation fails,
 * "list" : array of list items,
 * "listClassName": custom class name of list items container,
 * "listItemComponent": reference of list item component,
 * "listItemComponentProps": props of list item component
 * @returns
 * it will return the optimized version of list items for visible window
 */
export default function RenderList({
  defaultListItemHeight = 82,
  list,
  listClassName = "",
  listItemComponent,
  listItemComponentProps,
}) {
  // References
  const listRef = useRef({});
  const rowHeights = useRef({});

  /**
    ::: _setRowHeight ::: 
    This function receives two arguments, 
    the row "index" and the needed row "height size", with that we will then “recompute” 
    our list rows and we will create an object that will save the size based 
    on the row index resulting in something like this:
  */
  /**
    ::: resetAfterIndex ::: 
    This function is a part of the "react-window" methods and 
    it can be called by the list reference, this will cause the list to recalculate 
    it‘s row heights, but when the recalculation happens we will have the row heights 
    we need, not some fixed heights!
  */
  function _setRowHeight(index, size) {
    listRef.current.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }

  function Row({ index, style }) {
    /**
      We need to use a row reference to trigger and 
      call the "_setRowHeight" function, every list item and consequently in the rowRef, 
      so we take advantage of the react hook useEffect, that is basically listening to changes 
      and at that time we get and set the needed row height!
    */
    const rowRef = useRef({});

    useEffect(() => {
      if (rowRef.current) {
        _setRowHeight(index, rowRef.current.clientHeight);
      }
      // eslint-disable-next-line
    }, [rowRef]);

    return (
      <div className={listClassName} style={style}>
        {React.createElement(
          listItemComponent,
          {
            ...listItemComponentProps,
            listItemRef: rowRef,
            field: list[index],
          },
          null,
        )}
      </div>
    );
  }

  /**
    ::: _getRowHeight :::
    This function is the one who will control the row height that 
    we want to render dynamically in our list taking into account the space 
    that our messages require.
  */
  function _getRowHeight(index) {
    return rowHeights.current[index] || defaultListItemHeight;
  }

  return (
    <AutoSizer>
      {({ height, width }) => (
        <List
          className="List"
          height={height - 10} // wrapper's height
          itemCount={list.length}
          itemSize={_getRowHeight} // list item's height
          ref={listRef}
          width={width}>
          {Row}
        </List>
      )}
    </AutoSizer>
  );
}
